import { useState, useMemo, useRef } from "react";
import { DatesProvider } from "dates/DatesContext";
import { useLocation, Redirect } from "react-router-dom";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { Col, Row } from "react-bootstrap";
import { DASHBOARD_LINKS, DSP_DASHBOARD_BREADCRUMB_SPEC } from "utils/dashboardLinks";
import { useDates } from "dates/DatesContext";
import { useModuleEnabled } from "redux/selectors/modules";
import useDSPCampaignData from "./useDSPCampaignData";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { buildDSPMetric, getDSPMetricSpec } from "./metrics";
import Table from "views/merchandise/tables/BulkActionsTable";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import moment from "moment";
import TitleLink from "components/core/basic/TitleLink";
import { useAppSelector } from "redux/hooks";
import useDSPCreativeData from "./useDSPCreativeData";
import DSPCreativesTable from "./DSPCreativesTable";
import { DSP_ORDER_STATE_FILTER, DSP_ORDER_STATUS_FILTER } from "./dspLifecycleFilters";
import { buildDateColumn } from "views/merchandise/columns/buildDateColumn";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import DSPOverlapDashboard from "./DSPOverlapDashboad";
import CenteredModal from "components/core/basic/CenteredModal";
import DSPOrderForm from "./forms/DSPOrderForm";
import { Button } from "components/core/basic/Button";
import { useQueryClient } from "@tanstack/react-query";
import DSPGeoDashboard from "./DSPGeoDashboard";
import Tabs from "components/core/basic/Tabs";
import useQueryParams from "hooks/useQueryParams";
import { GOAL_KPI_LOOKUP } from "./constants";
import { css } from "../../../styled-system/css";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const Dashboard = () => {
  const [showOrderCreationModal, setShowOrderCreationModal] = useState(false);
  const { dspAdvertiser } = useDSPSellerInfo();

  const [tab, setTab] = useQueryParams("tab", "orders");

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const hasDSPModule = useModuleEnabled("DSP");

  const tableRef = useRef();
  const { search } = useLocation();
  const queryClient = useQueryClient();

  const { group, start, end } = useDates();
  const TABLE_ID = "dspCampaigns";
  const filterProps = useCustomTableFilters(TABLE_ID);
  const urlParams = new URLSearchParams(search);

  const ADDITIONAL_FILTERS = [DSP_ORDER_STATUS_FILTER, DSP_ORDER_STATE_FILTER];

  const {
    data: dspCampaignData,
    metadata: dspCampaignMetadata,
    isLoading: isDSPCampaignDataLoading,
  } = useDSPCampaignData("campaign_id");

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true);

  const { data: dspOverviewData, isLoading: isDSPOverviewDataLoading } = useDSPCampaignData("org_id");
  const { data: dspTimeseriesData, isLoading: isDSPTimeseriesDataLoading } = useDSPCampaignData(`org_id,${group}`);

  const isLoading = isDSPCampaignDataLoading || isDSPOverviewDataLoading || isDSPTimeseriesDataLoading;

  const data = useMemo(() => {
    return {
      total: dspOverviewData?.[0] ?? {},
      date: dspTimeseriesData
        ?.sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        })
        ?.map((d) => ({ ...d, start_date: d.date })),
      campaigns: dspCampaignData,
    };
  }, [dspCampaignData, dspOverviewData, dspTimeseriesData]);

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};
      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  const dspCampaignTableData = useMemo(() => {
    const combinedData = dspCampaignMetadata
      ?.map((campaignMetadata) => {
        const stats = dspCampaignData?.find((row) => row.campaign_id === campaignMetadata.campaign_id) ?? {};
        return { ...campaignMetadata, ...stats };
      })
      ?.map((row) => {
        const startDate = moment.min(row.flights?.map((f) => moment(f.startDateTime)))?.format();
        const endDate = moment.max(row.flights?.map((f) => moment(f.endDateTime)))?.format();
        const createdOn = moment(row.creation_date_time)?.format();
        const formattedState = row.delivery_status === "ENDED" ? "ENDED" : row.state;

        return {
          ...row,
          dsp_order_start_date: startDate,
          dsp_order_end_date: endDate,
          creation_date_time: createdOn,
          order_state: formattedState,
        };
      });

    return combinedData;
  }, [dspCampaignData, dspCampaignMetadata]);

  const breadCrumbItems = [
    {
      name: "DSP Dashboard",
      href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
      unsorted: true,
      dropdownLinkOptions: DASHBOARD_LINKS,
      dropdownSubtitle: "Dashboards",
    },
  ];

  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_sales", "dsp_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_sales", "dsp_total_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_ecpm", "dsp_ecpm_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_impressions", "dsp_impressions_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_clicks", "dsp_clicks_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_cost", "dsp_cost_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_purchases", "dsp_total_purchases_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_units", "dsp_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_units", "dsp_total_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_ctr", "dsp_ctr_delta"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_acos", "dsp_total_acos_delta"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_roas", "dsp_total_roas_delta"),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_total_detail_page_views",
        "dsp_total_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_total_detail_page_view_rate",
        "dsp_total_detail_page_view_rate_delta",
      ),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_add_to_cart", "dsp_total_add_to_cart_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_new_to_brand_detail_page_views",
        "dsp_new_to_brand_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_new_to_brand_detail_page_views_rate",
        "dsp_new_to_brand_detail_page_views_rate_delta",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_total_new_to_brand_dpvs",
        "dsp_total_new_to_brand_dpvs_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        data,
        "dsp_total_new_to_brand_dpv_rate",
        "dsp_total_new_to_brand_dpv_rate_delta",
      ),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_brand_searches", "dsp_brand_searches_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_brand_searches_rate", "dsp_brand_searches_rate_delta"),
    ];
  }, [data, marketPlace]);

  // Table columns
  const columnSpecs = useMemo(() => {
    return [
      buildTextColumn("delivery_status", "Delivery Status", {
        formatter: (value) => toTitleCase(snakeCaseToSentenceCase(value)),
        checked: false,
        toolTip: `The campaign's delivery status`,
        width: 200,
      }),
      buildTextColumn("order_state", "State", {
        formatter: (value) => toTitleCase(snakeCaseToSentenceCase(value)),
        toolTip: `The campaign's state`,
      }),

      buildDateColumn("dsp_order_start_date", "Start Date", {
        checked: false,
        toolTip: `The start date of the order's first flight`,
      }),
      buildDateColumn("dsp_order_end_date", "End Date", {
        checked: false,
        toolTip: `The end date of the order's last flight`,
      }),
      {
        key: "goal",
        compareKey: null,
        options: {
          metric: {
            id: "goal",
            format: "text",
            accessor: (d) => d,
            name: "Goal",
          },
          checked: false,
          width: 160,
          formatter: (v) => {
            // For now only showing goal if it's a running order, otherwise we don't have accurate data to compare against the goal
            const showGoalCompare = v.state === "ACTIVE" && v.delivery_status === "DELIVERING";

            const goalSpec = GOAL_KPI_LOOKUP[v.optimization?.goalSetting.kpi];
            if (!goalSpec) return;

            let goalTarget = v.optimization?.goalSetting?.targetKpi;

            if (goalSpec.format === "percent") {
              goalTarget /= 100;
            }

            const spec = getDSPMetricSpec(marketPlace, goalSpec.accessor);

            const onTarget = goalSpec.lower ? v[goalSpec.accessor] < goalTarget : v[goalSpec.accessor] > goalTarget;

            return (
              <span>
                <small>
                  Actual {goalSpec.label}: {showGoalCompare ? spec.formatter(v[goalSpec.accessor]) : "-"}
                </small>
                <br />
                <small>
                  {showGoalCompare ?
                    <>
                      {onTarget ?
                        <span className={css({ color: "trellisPalette.12", fontWeight: "bold" })}>On Target</span>
                      : <span className={css({ color: "trellisPalette.17", fontWeight: "bold" })}>Off Target</span>}
                    </>
                  : null}{" "}
                  (vs. Goal: {spec.formatter(goalTarget)})
                </small>
              </span>
            );
          },
        },
      },
      { key: "dsp_sales", compareKey: "dsp_sales_percent" },
      { key: "dsp_total_sales", compareKey: "dsp_total_sales_percent" },
      { key: "dsp_ecpm", compareKey: "dsp_ecpm_percent" },
      { key: "dsp_impressions", compareKey: "dsp_impressions_percent" },
      { key: "dsp_clicks", compareKey: "dsp_clicks_percent" },
      { key: "dsp_cost", compareKey: "dsp_cost_percent" },
      { key: "dsp_total_purchases", compareKey: "dsp_total_purchases_percent" },
      { key: "dsp_units", compareKey: "dsp_units_percent" },
      {
        key: "dsp_total_units",
        compareKey: "dsp_total_units_percent",
        options: {
          checked: false,
        },
      },
      { key: "dsp_ctr", compareKey: "dsp_ctr_delta" },
      {
        key: "dsp_total_acos",
        compareKey: "dsp_total_acos_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_roas",
        compareKey: "dsp_total_roas_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_views",
        compareKey: "dsp_total_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_view_rate",
        compareKey: "dsp_total_detail_page_view_rate_delta",
        options: {
          checked: false,
        },
      },
      {
        key: "dsp_total_add_to_cart",
        compareKey: "dsp_total_add_to_cart_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views",
        compareKey: "dsp_new_to_brand_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views_rate",
        compareKey: "dsp_new_to_brand_detail_page_views_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpvs",
        compareKey: "dsp_total_new_to_brand_dpvs_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpv_rate",
        compareKey: "dsp_total_new_to_brand_dpv_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches",
        compareKey: "dsp_brand_searches_percent",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches_rate",
        compareKey: "dsp_brand_searches_rate_delta",
        options: { checked: false },
      },

      buildDateColumn("creation_date_time", "Created On", {
        toolTip: `The campaign's creation date`,
      }),
    ];
  }, [marketPlace]);

  if (
    hasDSPModule &&
    !breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === DSP_DASHBOARD_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0].dropdownLinkOptions.push(DSP_DASHBOARD_BREADCRUMB_SPEC);
  }

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Redirect to="/user/dashboard" />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title="DSP Dashboard"
            initialSelectedMetric={["dsp_sales", "dsp_impressions", "dsp_ecpm", "dsp_total_purchases", "dsp_clicks"]}
            data={data}
            metrics={overviewMetrics}
            group={group}
            loading={isLoading}
            showLoadingSpinner={true}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {start.format("MMMM Do YYYY")}
                {" - "}
                {end.format("MMMM Do YYYY")}
              </>
            }
            showSkeletonLoadingEffect={true}
          >
            <Col xs={12}>
              <Tabs
                selectedTab={tab}
                onTabChange={setTab}
                tabs={[
                  { key: "orders", label: "DSP Orders" },
                  { key: "creatives", label: "DSP Creatives" },
                  { key: "overlap", label: "Audience Overlap" },
                  { key: "geography", label: "Geography" },
                ]}
              />

              {tab === "orders" && (
                <>
                  <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
                    <CenteredModal
                      adPlanModal={true}
                      show={showOrderCreationModal}
                      closeModal={() => {
                        setShowOrderCreationModal(false);
                      }}
                      content={
                        <Row>
                          <Col xs={12}>
                            <DSPOrderForm
                              initialFormData={{}}
                              createMode={true}
                              onCancel={() => {
                                setShowOrderCreationModal(false);
                              }}
                              onSuccessfulSubmit={() => {
                                setShowOrderCreationModal(false);
                                queryClient.invalidateQueries({
                                  queryKey: ["dspCampaignData"],
                                });
                              }}
                              dspAdvertiserId={dspAdvertiser?.advertiser_id}
                            />
                          </Col>
                        </Row>
                      }
                    />
                    <Button
                      onPress={() => {
                        setShowOrderCreationModal(true);
                      }}
                    >
                      Create a New Order
                    </Button>
                  </Col>
                  <Col xs={12}>
                    <Table
                      marketPlace={marketPlace}
                      tableRef={tableRef}
                      tableId={TABLE_ID}
                      isExpandable={false}
                      isSelectable={false}
                      titleCol={{
                        id: "order",
                        isStatic: true,
                        Header: "Order",
                        accessor: "text",
                        format: "text",
                        style: { width: "250px" },
                        width: 250,
                        Cell: ({ row }) => {
                          const { name: dspCampaignName, campaign_id: campaignId } = row?._original ?? {};

                          let campaignDetailsURL = `/user/dsp/${dspAdvertiser?.id}/orders/${campaignId}`;

                          if (urlParams.toString()) {
                            campaignDetailsURL = `${campaignDetailsURL}?${urlParams.toString()}`;
                          }

                          return (
                            <TitleLink
                              to={campaignDetailsURL}
                              style={{
                                textDecoration: "none",
                                fontWeight: "500",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                              }}
                              title={dspCampaignName}
                            />
                          );
                        },
                        Filter: () => null,
                      }}
                      data={dspCampaignTableData}
                      columnSpecs={columnSpecs}
                      getSearchCriteria={(row) => {
                        const { name: dspCampaignName, campaign_id: campaignId } = row ?? {};
                        return `${dspCampaignName} ${campaignId}`;
                      }}
                      tableRowDescription={"Campaigns"}
                      defaultSorted={[{ id: "dsp_sales", desc: true }]}
                      isLoading={isLoading}
                      filterWidget={true}
                      additionalFilterOptions={ADDITIONAL_FILTERS}
                      excludedFilterColumns={["order", "delivery_status", "order_state", "goal"]}
                      hideFilterToggleIcon={true}
                      hideResetFiltersIcon={false}
                      {...filterProps}
                      handleBulkChanges={() => {}}
                      minRows={1}
                    />
                  </Col>
                </>
              )}
              {tab === "creatives" && (
                <Col xs={12}>
                  <DSPCreativesTable data={dspCreativesData} isLoading={isDSPCreativeDataLoading} />
                </Col>
              )}
              {tab === "geography" && (
                <Col xs={12}>
                  <DSPGeoDashboard all />
                </Col>
              )}
              {tab === "overlap" && (
                <Col xs={12}>
                  <DSPOverlapDashboard />
                </Col>
              )}
            </Col>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPDashboard = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default DSPDashboard;
